import { useEffect, useMemo, useState } from 'react';
import { X, Banknote, TrendingDown, AlertTriangle } from 'lucide-react';
import { ModalRoot } from '@/components/ui/ModalRoot';
import { Z_MODALS } from '@/lib/zIndex';
import { useGameStore } from '@/state/gameStore';
import { formatEuro } from '@/lib/format';
import {
  LOAN_MIN_AMOUNT,
  LOAN_MAX_AMOUNT,
  LOAN_INTEREST_RATE_WEEKLY,
  availableLoanCapacity,
  totalOutstanding,
  maxOutstandingLoan,
} from '@/data/loans';

interface LoanDialogProps {
  open: boolean;
  onClose: () => void;
}

const PRESET_AMOUNTS = [100_000, 500_000, 1_000_000, 5_000_000, 10_000_000];

/**
 * Phase 8.3.1 — Bank loan management.
 *
 * Single modal that shows: outstanding loans, repayment buttons, take-loan
 * form. The maximum outstanding total is governed by `dailyUpkeep`.
 */
export function LoanDialog({ open, onClose }: LoanDialogProps) {
  const credits = useGameStore((s) => s.credits);
  const dailyUpkeep = useGameStore((s) => s.dailyUpkeep);
  const loans = useGameStore((s) => s.loans);
  const mode = useGameStore((s) => s.mode);
  const takeLoan = useGameStore((s) => s.takeLoan);
  const repayLoan = useGameStore((s) => s.repayLoan);
  const pushToast = useGameStore((s) => s.pushToast);

  const [amount, setAmount] = useState<number>(LOAN_MIN_AMOUNT);

  useEffect(() => {
    if (!open) return;
    setAmount(LOAN_MIN_AMOUNT);
  }, [open]);

  const cap = useMemo(() => maxOutstandingLoan(dailyUpkeep), [dailyUpkeep]);
  const outstanding = useMemo(() => totalOutstanding(loans), [loans]);
  const capacity = useMemo(
    () => availableLoanCapacity(loans, dailyUpkeep),
    [loans, dailyUpkeep],
  );

  if (!open) return null;

  const isSandbox = mode === 'sandbox';

  const handleTake = () => {
    if (isSandbox) {
      pushToast({ kind: 'info', text: 'Sandbox-Modus: Kredite sind deaktiviert.' });
      return;
    }
    if (amount < LOAN_MIN_AMOUNT || amount > LOAN_MAX_AMOUNT) {
      pushToast({
        kind: 'error',
        text: `Kreditbetrag muss zwischen ${formatEuro(LOAN_MIN_AMOUNT)} und ${formatEuro(LOAN_MAX_AMOUNT)} liegen.`,
      });
      return;
    }
    if (amount > capacity) {
      pushToast({
        kind: 'error',
        text: `Über deinem Kreditrahmen (max ${formatEuro(capacity)} verfügbar).`,
      });
      return;
    }
    const ok = takeLoan(amount);
    if (ok) {
      pushToast({
        kind: 'success',
        text: `Kredit über ${formatEuro(amount)} aufgenommen.`,
      });
    } else {
      pushToast({ kind: 'error', text: 'Kredit konnte nicht aufgenommen werden.' });
    }
  };

  const handleRepay = (loanId: string, payAmount: number, label: string) => {
    if (credits < payAmount) {
      pushToast({ kind: 'error', text: 'Nicht genug Credits für diese Tilgung.' });
      return;
    }
    const ok = repayLoan(loanId, payAmount);
    if (ok) {
      pushToast({ kind: 'success', text: `Tilgung ${label}: -${formatEuro(payAmount)}` });
    }
  };

  return (
    <ModalRoot
      onClose={onClose}
      zIndex={Z_MODALS}
      contentClass="w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-lg border border-border-strong bg-bg-secondary p-6"
    >
      <div className="mb-4 flex items-start justify-between">
        <div>
          <p className="flex items-center gap-2 text-xs uppercase tracking-widest text-amber-400">
            <Banknote className="h-3.5 w-3.5" aria-hidden /> Bank · Kreditverwaltung
          </p>
          <h3 className="mt-1 font-display text-xl font-semibold">Kredite</h3>
          <p className="mt-1 text-xs text-fg-muted">
            5 % Zinsen pro Spielwoche auf das offene Restkapital. Mehrere Kredite möglich.
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Schließen"
          className="text-fg-muted transition hover:text-fg-primary"
        >
          <X className="h-4 w-4" aria-hidden />
        </button>
      </div>

      {isSandbox && (
        <div className="mb-4 flex items-start gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-xs text-amber-200">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" aria-hidden />
          Im Sandbox-Modus stehen unbegrenzte Credits zur Verfügung — Kredite sind deshalb deaktiviert.
        </div>
      )}

      <div className="mb-5 grid grid-cols-3 gap-2">
        <Stat label="Offen gesamt" value={formatEuro(outstanding)} tone="warn" />
        <Stat label="Kreditrahmen" value={formatEuro(cap)} tone="muted" />
        <Stat label="Noch verfügbar" value={formatEuro(capacity)} tone="ok" />
      </div>

      <section className="mb-5">
        <h4 className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-fg-muted">
          Laufende Kredite ({loans.length})
        </h4>
        {loans.length === 0 ? (
          <p className="rounded-md border border-dashed border-border-subtle py-6 text-center text-xs text-fg-muted">
            Keine offenen Kredite.
          </p>
        ) : (
          <ul className="space-y-2">
            {loans.map((l) => (
              <li
                key={l.id}
                className="rounded-md border border-border-subtle bg-bg-elevated/40 p-3"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-semibold">
                      Kredit {formatEuro(l.principalAmount)}
                    </p>
                    <p className="mt-0.5 text-[10px] text-fg-muted">
                      Restschuld:{' '}
                      <span className="font-semibold text-accent-emergency">
                        {formatEuro(l.remainingPrincipal)}
                      </span>
                      {l.accruedInterest > 0 && (
                        <>
                          {' '}
                          · Zinsen bisher:{' '}
                          <span className="text-amber-300">
                            +{formatEuro(l.accruedInterest)}
                          </span>
                        </>
                      )}
                    </p>
                  </div>
                  <div className="flex flex-shrink-0 gap-1.5">
                    <button
                      type="button"
                      onClick={() =>
                        handleRepay(
                          l.id,
                          Math.min(100_000, l.remainingPrincipal),
                          formatEuro(Math.min(100_000, l.remainingPrincipal)),
                        )
                      }
                      disabled={isSandbox}
                      className="rounded-md border border-border-subtle bg-bg-elevated px-2 py-1 text-[10px] font-semibold transition hover:border-accent-emergency hover:text-accent-emergency disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      -100k
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        handleRepay(
                          l.id,
                          Math.min(1_000_000, l.remainingPrincipal),
                          formatEuro(Math.min(1_000_000, l.remainingPrincipal)),
                        )
                      }
                      disabled={isSandbox}
                      className="rounded-md border border-border-subtle bg-bg-elevated px-2 py-1 text-[10px] font-semibold transition hover:border-accent-emergency hover:text-accent-emergency disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      -1M
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        handleRepay(l.id, l.remainingPrincipal, 'voll')
                      }
                      disabled={isSandbox || credits < l.remainingPrincipal}
                      className="rounded-md bg-accent-emergency px-2 py-1 text-[10px] font-semibold text-white transition hover:bg-accent-emergency-hover disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      voll
                    </button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section>
        <h4 className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-fg-muted">
          Neuen Kredit aufnehmen
        </h4>
        <div className="rounded-md border border-border-subtle bg-bg-elevated/40 p-3">
          <div className="mb-3 flex items-center gap-2">
            <input
              type="range"
              min={LOAN_MIN_AMOUNT}
              max={LOAN_MAX_AMOUNT}
              step={50_000}
              value={amount}
              onChange={(e) => setAmount(Number(e.target.value))}
              className="flex-1 accent-amber-400"
            />
            <span className="w-32 text-right font-display text-base font-bold tabular-nums">
              {formatEuro(amount)}
            </span>
          </div>
          <div className="mb-3 flex flex-wrap gap-1.5">
            {PRESET_AMOUNTS.map((v) => (
              <button
                key={v}
                type="button"
                onClick={() => setAmount(v)}
                className={[
                  'rounded-md border px-2 py-1 text-[10px] font-semibold transition',
                  amount === v
                    ? 'border-amber-400 bg-amber-400/10 text-amber-200'
                    : 'border-border-subtle bg-bg-elevated text-fg-muted hover:border-border-strong hover:text-fg-secondary',
                ].join(' ')}
              >
                {formatEuroShort(v)}
              </button>
            ))}
          </div>
          <p className="mb-3 text-[10px] text-fg-muted">
            <TrendingDown className="mr-1 inline h-3 w-3 text-amber-400" aria-hidden />
            Bei diesem Betrag fallen ca.{' '}
            <span className="font-semibold text-amber-300">
              {formatEuro(Math.round(amount * LOAN_INTEREST_RATE_WEEKLY))}/Spielwoche
            </span>{' '}
            Zinsen an.
          </p>
          <button
            type="button"
            onClick={handleTake}
            disabled={isSandbox || amount > capacity}
            className={[
              'flex w-full items-center justify-center gap-2 rounded-md px-3 py-2 text-sm font-semibold text-white transition',
              isSandbox || amount > capacity
                ? 'cursor-not-allowed bg-bg-elevated text-fg-muted'
                : 'bg-amber-500 hover:bg-amber-600',
            ].join(' ')}
          >
            <Banknote className="h-4 w-4" aria-hidden />
            {isSandbox
              ? 'Im Sandbox deaktiviert'
              : amount > capacity
                ? 'Über Kreditrahmen'
                : `Kredit über ${formatEuro(amount)} aufnehmen`}
          </button>
        </div>
      </section>
    </ModalRoot>
  );
}

function Stat({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone: 'warn' | 'ok' | 'muted';
}) {
  const toneClass =
    tone === 'warn'
      ? 'text-accent-emergency'
      : tone === 'ok'
        ? 'text-status-available'
        : 'text-fg-secondary';
  return (
    <div className="rounded-md border border-border-subtle bg-bg-elevated/40 p-3 text-center">
      <p className="text-[9px] uppercase tracking-wider text-fg-muted">{label}</p>
      <p className={['mt-0.5 font-display text-sm font-bold tabular-nums', toneClass].join(' ')}>
        {value}
      </p>
    </div>
  );
}

function formatEuroShort(v: number): string {
  if (v >= 1_000_000) return `${(v / 1_000_000).toLocaleString('de-DE', { maximumFractionDigits: 1 })} M`;
  if (v >= 1_000) return `${Math.round(v / 1_000)} k`;
  return String(v);
}
